import { Pencil, Trash2 } from "lucide-react";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/shadcn/pagination";
import type { ContactNote } from "@/types/domain";

const PAGE_SIZE = 10;

type Props = {
  notes: ContactNote[];
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  onEdit: (note: ContactNote) => void;
  onDelete: (noteId: string) => void;
};

export const ContactNotesList = ({ notes, totalCount, page, onPageChange, onEdit, onDelete }: Props) => {
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <>
      <ul className="space-y-3 mt-2">
        {notes.map((note) => (
          <li key={note.id} className="rounded border border-gray-200 p-3">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="min-w-0">
                <span className="text-sm font-medium text-gray-800">{note.subject || "Note"}</span>
                {note.modifiedDate && (
                  <span className="text-xs text-muted-foreground ml-2 align-middle">{note.modifiedDate}</span>
                )}
                {note.modifiedByName && (
                  <span className="text-xs text-muted-foreground ml-2 align-middle">by {note.modifiedByName}</span>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  type="button"
                  title="Edit note"
                  className="p-1 text-muted-foreground hover:text-blue-600 transition-colors"
                  onClick={() => onEdit(note)}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  title="Delete note"
                  className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                  onClick={() => onDelete(note.id)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
            {note.body && (
              <p className="text-sm text-gray-700 whitespace-pre-wrap wrap-break-word">{note.body}</p>
            )}
          </li>
        ))}
      </ul>
      {totalPages > 1 && (
        <Pagination className="mt-3">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => { e.preventDefault(); onPageChange(page - 1); }}
                aria-disabled={page === 1}
                className={page === 1 ? "pointer-events-none opacity-40" : "cursor-pointer"}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="text-sm text-muted-foreground px-2">
                {page} / {totalPages} ({totalCount} notes)
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                onClick={(e) => { e.preventDefault(); onPageChange(page + 1); }}
                aria-disabled={page >= totalPages}
                className={page >= totalPages ? "pointer-events-none opacity-40" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};
